/**
 * presetApi.js — thin async wrappers around the /api/presets endpoints.
 *
 * Extracted from BrainwaveGenerator.jsx (Phase 4 refactor). Toast/UI handling
 * stays in the component; this module is just IO. All functions throw on
 * non-2xx so callers can use try/catch with a single error path.
 *
 * Offline support: fetchAllPresets() also writes the latest free + premium
 * library to localStorage on every successful fetch and falls back to that
 * cached copy when the network is unreachable. The service worker
 * (public/service-worker.js) handles in-session caching via a stale-while-
 * revalidate strategy on /api/presets; the localStorage layer here exists
 * as a second-line defense for browsers/sessions where the SW hasn't yet
 * installed (first-ever visit), is disabled (some private modes), or has
 * been evicted. Users skip this whole path on first ever visit but the
 * second visit onward will hydrate instantly even before the network
 * resolves. User-uploaded presets are deliberately excluded — they're
 * privacy-sensitive and shouldn't sit in localStorage on a shared device.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage key + max age for the offline preset backup. 30 days is generous
// enough that infrequent users still see their library, but short enough
// that pseudo-stale curated presets eventually refresh.
const OFFLINE_CACHE_KEY = 'neurowave_presets_cache_v1';
const OFFLINE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const writeOfflineCache = (free, premium) => {
  try {
    localStorage.setItem(
      OFFLINE_CACHE_KEY,
      JSON.stringify({ free, premium, ts: Date.now() })
    );
  } catch {
    // Quota exceeded or storage disabled — non-critical.
  }
};

const readOfflineCache = () => {
  try {
    const raw = localStorage.getItem(OFFLINE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > OFFLINE_MAX_AGE_MS) return null;
    if (!Array.isArray(parsed.free) || !Array.isArray(parsed.premium)) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** Fetch presets for a single category ('free' | 'premium' | 'user'). */
export async function fetchPresetsByCategory(category, limit = 100) {
  const resp = await axios.get(`${API}/presets`, { params: { category, limit } });
  return resp.data;
}

/**
 * Fetch all three categories in parallel; returns {free, premium, user, all}.
 *
 * On network failure, transparently falls back to the localStorage offline
 * cache (free + premium only, ≤30 days old). User presets always require a
 * network because they're per-account and never cached locally — if you're
 * offline, you simply see an empty user list and the toast in the caller
 * handles the messaging via the OfflineIndicator banner.
 */
export async function fetchAllPresets() {
  try {
    const [free, premium, user] = await Promise.all([
      fetchPresetsByCategory('free'),
      fetchPresetsByCategory('premium'),
      fetchPresetsByCategory('user'),
    ]);
    writeOfflineCache(free, premium);
    return { free, premium, user, all: [...free, ...premium, ...user], offline: false };
  } catch (err) {
    const cached = readOfflineCache();
    if (cached) {
      const ageDays = Math.round((Date.now() - cached.ts) / 86400000);
      console.log(`📋 Loaded presets from offline cache (${ageDays}d old)`);
      return {
        free: cached.free,
        premium: cached.premium,
        user: [],
        all: [...cached.free, ...cached.premium],
        offline: true,
      };
    }
    throw err;
  }
}

/** Trigger seeding of built-in presets on the server. Idempotent. */
export async function initializeBuiltinPresets() {
  const token = localStorage.getItem('neurowave_admin_token');
  return axios.post(`${API}/presets/initialize-builtin`, null, token ? { headers: { 'X-Admin-Token': token } } : undefined);
}

/** Delete a user preset by id. Throws on 404/403. */
export async function deletePresetById(id) {
  const resp = await axios.delete(`${API}/presets/${id}`);
  return resp.status === 200;
}

/** Create a public share token for a preset. Returns { share_id }. */
export async function createShareLink(presetId) {
  const resp = await axios.post(`${API}/presets/share/${presetId}`);
  return resp.data;
}

/** Resolve a share token to the underlying preset payload. */
export async function fetchSharedPreset(shareId) {
  const resp = await axios.get(`${API}/presets/shared/${shareId}`);
  return resp.data;
}
